import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
    Button,
    Empty,
    Image,
    Input,
    Modal,
    message,
    Space,
    Spin,
    Tag,
    Typography,
} from 'antd';
import { DownOutlined, FullscreenExitOutlined, FullscreenOutlined, RobotOutlined, SendOutlined, UpOutlined, UserOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import type { Dayjs } from 'dayjs';
import { QRCodeSVG } from 'qrcode.react';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { useAuth } from '../context/AuthContext';
import '../style/Assistant.css';

const { Paragraph, Text } = Typography;

interface ReportChatAssistantProps {
    areaId?: number | null;
    areaName?: string | null;
    dateRange?: [Dayjs, Dayjs] | null;
    patternId?: number | null;
    patternPointId?: number | null;
    measurementId?: number | null;
}

interface BackendMessage {
    id?: number;
    sender: string;
    content: string;
    messageType?: string;
    metadata?: any;
    createdAt?: string;
}

interface AssistantMessage {
    id: string;
    sender: 'agent' | 'user';
    content: string;
    messageType?: string;
    metadata?: any;
    createdAt?: string;
    extraMeta?: Record<string, any>;
}

const formatDate = (value?: string) => {
    if (!value) return '';
    try {
        const date = new Date(value);
        return date.toLocaleString();
    } catch {
        return value;
    }
};

const extractImageUrls = (content?: string): string[] => {
    if (!content) return [];
    const urlRegex = /https?:\/\/[^\s)]+/g;
    const matches = content.match(urlRegex) || [];
    const imageUrls = matches.filter((raw) => {
        const cleaned = raw.replace(/[),.]+$/, '');
        const isImageExtension = /\.(png|jpg|jpeg|webp)(\?|$)/i.test(cleaned);
        const isMapboxStatic = cleaned.includes('api.mapbox.com/styles') && cleaned.includes('/static/');
        return isImageExtension || isMapboxStatic;
    });
    return Array.from(new Set(imageUrls.map((url) => url.replace(/[),.]+$/, ''))));
};

const buildAttachmentList = (messageItem: AssistantMessage): Array<{ url: string; label?: string }> => {
    const attachments = messageItem.metadata?.attachments || messageItem.metadata?.images || [];
    const list: Array<{ url: string; label?: string }> = Array.isArray(attachments)
        ? attachments
            .map((item: any) => ({
                url: item?.url || item?.accessUrl || item?.imageUrl,
                label: item?.label || item?.metricName || item?.name || item?.title,
            }))
            .filter((item: any) => typeof item.url === 'string')
        : [];
    const contentUrls = extractImageUrls(messageItem.content);
    contentUrls.forEach((url) => {
        if (!list.find((item) => item.url === url)) {
            list.push({ url });
        }
    });
    return list;
};

const normalizeMessages = (rawMessages: BackendMessage[] = []): AssistantMessage[] =>
    rawMessages.map((msg) => ({
        id: `server-${msg.id ?? `${msg.messageType || 'msg'}-${Date.now()}`}`,
        sender: msg.sender === 'agent' ? 'agent' : 'user',
        content: msg.content,
        messageType: msg.messageType,
        metadata: msg.metadata,
        createdAt: msg.createdAt,
    }));

const sortMessages = (messages: AssistantMessage[]): AssistantMessage[] =>
    messages
        .map((messageItem, index) => ({
            message: messageItem,
            index,
            timestamp: messageItem.createdAt ? new Date(messageItem.createdAt).getTime() : Number.MAX_SAFE_INTEGER,
        }))
        .sort((a, b) => (a.timestamp === b.timestamp ? a.index - b.index : a.timestamp - b.timestamp))
        .map((entry) => entry.message);

const mergeMessages = (currentMessages: AssistantMessage[], serverMessages: AssistantMessage[]): AssistantMessage[] => {
    const serverIds = new Set(serverMessages.map((messageItem) => messageItem.id));
    const pendingLocal = currentMessages.filter(
        (messageItem) => messageItem.id.startsWith('local-') && !serverIds.has(messageItem.id),
    );
    const dedupedPending = pendingLocal.filter(
        (local) => !serverMessages.some(
            (server) => server.sender === local.sender && server.content === local.content,
        ),
    );
    return sortMessages([...serverMessages, ...dedupedPending]);
};

const ReportChatAssistant: React.FC<ReportChatAssistantProps> = ({
    areaId,
    areaName,
    dateRange,
    patternId,
    patternPointId,
    measurementId,
}) => {
    const { currentUser } = useAuth();
    const [messages, setMessages] = useState<AssistantMessage[]>([]);
    const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
    const [chatBusy, setChatBusy] = useState(false);
    const [messageInput, setMessageInput] = useState('');
    const [collapsed, setCollapsed] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [whatsAppContextLoading, setWhatsAppContextLoading] = useState(false);
    const [whatsAppContextModalOpen, setWhatsAppContextModalOpen] = useState(false);
    const [whatsAppContextLink, setWhatsAppContextLink] = useState<string | null>(null);
    const [whatsAppContextMessage, setWhatsAppContextMessage] = useState<string | null>(null);
    const [whatsAppContextExpiresAt, setWhatsAppContextExpiresAt] = useState<string | null>(null);
    const pollingRef = useRef<number | null>(null);
    const pendingUserMessageRef = useRef<number | null>(null);
    const chatWindowRef = useRef<HTMLDivElement | null>(null);
    const currentCompanyId = currentUser?.currentCompanyId || currentUser?.companyId || null;

    const contextType = measurementId
        ? 'measurement'
        : patternPointId
            ? 'pattern_point'
            : patternId
                ? 'pattern'
                : areaId
                    ? 'area'
                    : null;

    const hasContext = Boolean(contextType && dateRange?.[0] && dateRange?.[1]);
    const whatsAppContext = useMemo(() => {
        if (measurementId) {
            return { contextType: 'measurement' as const, contextId: measurementId };
        }
        if (patternPointId) {
            return { contextType: 'pattern_point' as const, contextId: patternPointId };
        }
        if (areaId) {
            return { contextType: 'area' as const, contextId: areaId };
        }
        return null;
    }, [areaId, measurementId, patternPointId]);

    const contextLabel = useMemo(() => {
        if (!dateRange?.[0] || !dateRange?.[1]) return '';
        const rangeLabel = `${dateRange[0].format('MMM D, YYYY')} – ${dateRange[1].format('MMM D, YYYY')}`;
        if (contextType === 'measurement' && measurementId) {
            return `Measurement #${measurementId} • ${rangeLabel}`;
        }
        if (contextType === 'pattern_point' && patternPointId) {
            return `Pattern Point #${patternPointId} • ${rangeLabel}`;
        }
        if (contextType === 'pattern' && patternId) {
            return `Pattern #${patternId} • ${rangeLabel}`;
        }
        return rangeLabel;
    }, [contextType, dateRange, measurementId, patternPointId, patternId]);

    const stopConversationPolling = useCallback(() => {
        if (pollingRef.current) {
            window.clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    const hydrateConversation = useCallback((conversation: any) => {
        const rawMessages: BackendMessage[] = conversation?.messages || [];
        setActiveConversationId(conversation?.id || null);
        const normalized = normalizeMessages(rawMessages);
        setMessages(sortMessages(normalized));
    }, []);

    const fetchConversationDetail = useCallback(async (conversationId: number) => {
        try {
            const response = await axios.get(ENDPOINTS.CONVERSATION_DETAIL(conversationId), { withCredentials: true });
            const conversation = response.data?.data;
            if (conversation?.messages) {
                setMessages((prev) => mergeMessages(prev, normalizeMessages(conversation.messages)));
            }
            return conversation;
        } catch (error) {
            console.error('Unable to fetch conversation detail', error);
            return null;
        }
    }, []);

    const startConversationPolling = useCallback((conversationId: number) => {
        stopConversationPolling();
        setChatBusy(true);
        const startedAt = Date.now();

        const poll = async () => {
            const conversation = await fetchConversationDetail(conversationId);
            const messageList: BackendMessage[] = conversation?.messages || [];
            const lastAgentMessage = [...messageList].reverse().find((msg) => msg.sender === 'agent');
            const inProgressTypes = new Set(['analysis_status', 'tool_result']);
            const pendingAt = pendingUserMessageRef.current;
            const lastAgentTs = lastAgentMessage?.createdAt ? new Date(lastAgentMessage.createdAt).getTime() : null;
            const agentAfterPending = pendingAt ? (lastAgentTs !== null && lastAgentTs >= pendingAt) : true;
            const isFinalResponse = lastAgentMessage
                ? !inProgressTypes.has(lastAgentMessage.messageType || '') && agentAfterPending
                : false;
            const timedOut = Date.now() - startedAt > 120000;

            if (isFinalResponse || timedOut) {
                if (isFinalResponse) {
                    pendingUserMessageRef.current = null;
                }
                stopConversationPolling();
                setChatBusy(false);
            }
        };

        poll();
        pollingRef.current = window.setInterval(poll, 2000);
    }, [fetchConversationDetail, stopConversationPolling]);

    const startConversation = useCallback(async () => {
        if (!hasContext || !dateRange?.[0] || !dateRange?.[1]) {
            message.warning('Select a report to analyse first.');
            return;
        }
        stopConversationPolling();
        pendingUserMessageRef.current = null;
        setChatBusy(true);
        try {
            const payload: Record<string, any> = {
                channel: 'web',
                from: dateRange[0].startOf('day').toISOString(),
                to: dateRange[1].endOf('day').toISOString(),
            };
            if (areaId && contextType !== 'pattern_point') {
                payload.areaId = areaId;
            }
            if (contextType === 'measurement' && measurementId) {
                payload.measurementId = measurementId;
            } else if (contextType === 'pattern_point' && patternPointId) {
                payload.patternPointId = patternPointId;
            } else if (contextType === 'pattern' && patternId) {
                payload.patternId = patternId;
            }
            const response = await axios.post(ENDPOINTS.CONVERSATIONS, payload, { withCredentials: true });
            const conversation = response.data?.data;
            const initializationStatus = response.data?.initialization?.status;
            hydrateConversation(conversation);
            message.success('Analysis started');
            if (conversation?.id) {
                if (initializationStatus === 'reused') {
                    setChatBusy(false);
                } else {
                    startConversationPolling(conversation.id);
                }
            } else {
                setChatBusy(false);
            }
        } catch (error) {
            console.error('Unable to start conversation', error);
            message.error('Failed to start analysis');
            setChatBusy(false);
        }
    }, [
        areaId,
        dateRange,
        hasContext,
        hydrateConversation,
        startConversationPolling,
        stopConversationPolling,
        contextType,
        measurementId,
        patternPointId,
        patternId,
    ]);

    const startWhatsAppContextChat = useCallback(async () => {
        if (!whatsAppContext || !currentCompanyId || !dateRange?.[0] || !dateRange?.[1]) {
            message.error('Select a report context and date range first.');
            return;
        }
        setWhatsAppContextLoading(true);
        try {
            const response = await axios.post(
                ENDPOINTS.WHATSAPP_QR_CONTEXT,
                {
                    contextType: whatsAppContext.contextType,
                    contextId: whatsAppContext.contextId,
                    companyId: currentCompanyId,
                    from: dateRange[0].startOf('day').toISOString(),
                    to: dateRange[1].endOf('day').toISOString()
                },
                { withCredentials: true }
            );
            const data = response.data?.data || {};
            setWhatsAppContextLink(data.whatsappLink || null);
            setWhatsAppContextMessage(data.message || null);
            setWhatsAppContextExpiresAt(data.expiresAt || null);
            setWhatsAppContextModalOpen(true);
            message.success('WhatsApp context QR generated.');
        } catch (error: any) {
            console.error('Unable to generate WhatsApp context QR', error);
            message.error(error?.response?.data?.message || 'Unable to generate WhatsApp context QR.');
        } finally {
            setWhatsAppContextLoading(false);
        }
    }, [currentCompanyId, dateRange, whatsAppContext]);

    const handleSendMessage = useCallback(async () => {
        if (!activeConversationId || !messageInput.trim() || chatBusy) {
            return;
        }
        stopConversationPolling();
        const trimmed = messageInput.trim();
        pendingUserMessageRef.current = Date.now();
        const localMessage: AssistantMessage = {
            id: `local-${Date.now()}`,
            sender: 'user',
            content: trimmed,
            createdAt: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, localMessage]);
        setMessageInput('');

        setChatBusy(true);
        startConversationPolling(activeConversationId);
        try {
            const response = await axios.post(
                ENDPOINTS.CONVERSATION_MESSAGES(activeConversationId),
                {
                    content: trimmed,
                    sender: 'user',
                    messageType: 'text',
                },
                { withCredentials: true },
            );

            const savedMessage: BackendMessage | undefined = response.data?.data;
            if (savedMessage) {
                setMessages((prev) => {
                    const serverMessage = normalizeMessages([savedMessage])[0];
                    const hasLocal = prev.some((msg) => msg.id === localMessage.id);
                    if (hasLocal) {
                        return prev.map((msg) => (msg.id === localMessage.id ? { ...msg, id: serverMessage.id } : msg));
                    }
                    if (prev.some((msg) => msg.id === serverMessage.id)) {
                        return prev;
                    }
                    return sortMessages([...prev, serverMessage]);
                });
            }

            const agentResponse = response.data?.agentResponse;
            if (agentResponse?.content) {
                const agentMessage: AssistantMessage = {
                    id: `agent-${Date.now()}`,
                    sender: 'agent',
                    content: agentResponse.content,
                    messageType: 'analysis',
                    metadata: {
                        attachments: agentResponse.attachments || [],
                    },
                    extraMeta: {
                        tokensUsed: agentResponse.tokensUsed,
                        model: agentResponse.model,
                    },
                };
                setMessages((prev) => [...prev, agentMessage]);
                pendingUserMessageRef.current = null;
            }
        } catch (error) {
            console.error('Unable to send message', error);
            message.error('Message delivery failed');
            stopConversationPolling();
            setChatBusy(false);
        }
    }, [activeConversationId, chatBusy, messageInput, startConversationPolling, stopConversationPolling]);

    const renderMessage = (messageItem: AssistantMessage) => {
        const isAgent = messageItem.sender === 'agent';
        const attachments = buildAttachmentList(messageItem);
        return (
            <div key={messageItem.id} className={`assistant-message ${isAgent ? 'agent' : 'user'}`}>
                <div className="assistant-message-meta">
                    <Tag color={isAgent ? 'blue' : 'green'} icon={isAgent ? <RobotOutlined /> : <UserOutlined />}>
                        {isAgent ? 'Agent' : 'You'}
                    </Tag>
                    {messageItem.createdAt && (
                        <Text type="secondary" className="assistant-message-time">
                            {formatDate(messageItem.createdAt)}
                        </Text>
                    )}
                </div>
                <div className="assistant-message-body">
                    {isAgent ? (
                        <div className="markdown-content assistant-markdown">
                            <ReactMarkdown>{messageItem.content}</ReactMarkdown>
                        </div>
                    ) : (
                        <Paragraph className="assistant-message-text">{messageItem.content}</Paragraph>
                    )}
                    {attachments.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                            <Image.PreviewGroup>
                                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                    {attachments.map((attachment) => (
                                        <div key={attachment.url}>
                                            <Image
                                                src={attachment.url}
                                                alt={attachment.label || 'attachment'}
                                                style={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 6 }}
                                            />
                                            {attachment.label && (
                                                <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                                                    {attachment.label}
                                                </Text>
                                            )}
                                        </div>
                                    ))}
                                </Space>
                            </Image.PreviewGroup>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    useEffect(() => {
        return () => {
            stopConversationPolling();
        };
    }, [stopConversationPolling]);

    useEffect(() => {
        const node = chatWindowRef.current;
        if (!node) return;
        node.scrollTop = node.scrollHeight;
    }, [messages]);

    return (
        <div className={`reports-floating-chat${collapsed ? ' reports-floating-chat--collapsed' : ''}${expanded ? ' reports-floating-chat--expanded' : ''}`}>
            <div className="reports-floating-chat-card">
                <div className="reports-chat-header">
                    <div>
                        <Text strong>Report assistant</Text>
                        <Text type="secondary" className="reports-chat-context">
                            {areaName
                                ? `${areaName}${contextLabel ? ` • ${contextLabel}` : ''}`
                                : (contextLabel || 'Select a report to analyse')}
                        </Text>
                    </div>
                    <Space className="reports-chat-actions">
                        <Button
                            type="primary"
                            size="small"
                            onClick={startConversation}
                            loading={chatBusy && !activeConversationId}
                            disabled={!hasContext}
                            className="reports-chat-analyse-btn"
                        >
                            Analyse report
                        </Button>
                        <Button
                            size="small"
                            onClick={startWhatsAppContextChat}
                            loading={whatsAppContextLoading}
                            disabled={!hasContext || !whatsAppContext || !currentCompanyId}
                            className="reports-chat-whatsapp-btn"
                        >
                            Chat Through WhatsApp
                        </Button>
                        <Button
                            type="text"
                            size="small"
                            icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                            onClick={() => {
                                setExpanded((prev) => {
                                    const next = !prev;
                                    if (next) {
                                        setCollapsed(false);
                                    }
                                    return next;
                                });
                            }}
                            className="reports-chat-toggle-btn"
                        />
                        <Button
                            type="text"
                            size="small"
                            icon={collapsed ? <UpOutlined /> : <DownOutlined />}
                            onClick={() => {
                                if (expanded) {
                                    setExpanded(false);
                                    return;
                                }
                                setCollapsed((prev) => !prev);
                            }}
                            className="reports-chat-toggle-btn"
                        />
                    </Space>
                </div>
                <div className="reports-chat-body">
                    <div className="assistant-chat-window reports-chat-window" ref={chatWindowRef}>
                        {messages.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No messages yet" />
                        ) : (
                            messages.map(renderMessage)
                        )}
                    </div>
                    <div className="reports-chat-footer">
                        <Space.Compact className="assistant-input-row">
                            <Input.TextArea
                                rows={2}
                                value={messageInput}
                                onChange={(event) => setMessageInput(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' && !event.shiftKey) {
                                        event.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                placeholder={activeConversationId ? 'Ask a question about this report…' : 'Analyse a report to start chatting'}
                                disabled={!activeConversationId || chatBusy}
                            />
                            <Button
                                type="primary"
                                icon={<SendOutlined />}
                                onClick={handleSendMessage}
                                disabled={!activeConversationId || !messageInput.trim() || chatBusy}
                                loading={chatBusy && !!activeConversationId}
                            />
                        </Space.Compact>
                        {chatBusy && (
                            <div className="reports-chat-status">
                                <Spin size="small" />
                                <Text type="secondary">Working…</Text>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Modal
                title="Chat Through WhatsApp"
                open={whatsAppContextModalOpen}
                onCancel={() => setWhatsAppContextModalOpen(false)}
                footer={null}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Text>
                        Scan this QR code and send the generated message in WhatsApp to start this report context.
                    </Text>
                    {whatsAppContextLink && (
                        <div style={{ textAlign: 'center' }}>
                            <QRCodeSVG value={whatsAppContextLink} size={200} level="M" includeMargin />
                        </div>
                    )}
                    {whatsAppContextMessage && (
                        <Paragraph copyable={{ text: whatsAppContextMessage }}>
                            <Text strong>Message code:</Text> {whatsAppContextMessage}
                        </Paragraph>
                    )}
                    {whatsAppContextLink && (
                        <Space wrap>
                            <Button type="primary" href={whatsAppContextLink} target="_blank" rel="noreferrer">
                                Open WhatsApp
                            </Button>
                            <Button
                                onClick={() => {
                                    if (whatsAppContextLink) {
                                        navigator.clipboard?.writeText(whatsAppContextLink);
                                        message.success('WhatsApp link copied.');
                                    }
                                }}
                            >
                                Copy Link
                            </Button>
                        </Space>
                    )}
                    {whatsAppContextExpiresAt && (
                        <Text type="secondary">Expires: {formatDate(whatsAppContextExpiresAt)}</Text>
                    )}
                </Space>
            </Modal>
        </div>
    );
};

export default ReportChatAssistant;
